import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { AuthEffects } from './auth/auth.effects';
import { hydrateAuthState } from './auth/auth.hydrate';
import { persistAuthMetaReducer } from './auth/auth.persist';
import { authReducer } from './auth/auth.reducer';
import { authFeatureKey } from './auth/auth.state';

export * from './auth/auth.actions';
export * from './auth/auth.selectors';
export * from './auth/auth.state';
export * from './auth/auth-snapshot.service';

/**
 * Root store wiring.
 *
 * Registered through the functional API in AppModule's providers rather than
 * StoreModule.forRoot(), because provideStore initialises the store from an
 * ENVIRONMENT_INITIALIZER when the root injector is constructed. That is an
 * ordering guarantee independent of position in a twenty-entry imports array —
 * and this migration depends on hydration beating the first HTTP request.
 */
export function provideAppStore(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideStore(
      { [authFeatureKey]: authReducer },
      {
        // The FUNCTION, not its result: NgRx calls it during root-injector
        // construction, so the session is in the store before AppComponent
        // bootstraps or the router activates anything.
        initialState: () => ({ [authFeatureKey]: hydrateAuthState() }),
        metaReducers: [persistAuthMetaReducer],
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          // Off: user and organisation payloads come straight off the wire and
          // may carry non-plain values. Enforcing serializability here would be a
          // second, unrelated migration.
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictActionTypeUniqueness: true,
        },
      }
    ),
    provideEffects([AuthEffects]),
    provideStoreDevtools({
      maxAge: 25,
      // The slice holds a live JWT and the full user record, so production gets
      // a one-way log rather than a time-travelling, inspectable session.
      logOnly: environment.production,
      autoPause: true,
      connectInZone: true, // zone.js app, not zoneless
      trace: !environment.production,
    }),
  ]);
}
